import React, { useCallback, useEffect, useRef, useState } from 'react';

import ColorPicker from 'components/ui/ColorPicker/ColorPicker';

import { brandColorPalette } from 'theme/theme';
import { Button } from '../../../components/ui/Button';
import { Icon } from '../../../components/ui/Icon';
import { Toolbar } from '../../../components/ui/Toolbar';
import {
  ChartConfig,
  ChartData,
  ChartMode,
  ChartSeries,
  MultipleSeriesChartData,
  PieSliceText,
  PieStyle,
} from '../Chart';
import { useChartImageGenerator } from '../hooks/useChartImageGenerator';
import { ChartPreview } from './ChartPreview';

interface ChartEditorProps {
  config: ChartConfig;
  onSave: (config: ChartConfig, imagePath?: string) => void;
  onCancel: () => void;
}

const CHART_TYPES = [
  { value: 'pie', label: 'Pizza' },
  { value: 'column', label: 'Barras' },
  { value: 'bar', label: 'Barras Horizontais' },
  { value: 'line', label: 'Linha' },
];

const CHART_MODES: { value: ChartMode; label: string }[] = [
  { value: 'simple', label: 'Simples' },
  { value: 'multiple', label: 'Múltiplas Séries' },
];

const PIE_STYLES: { value: PieStyle; label: string }[] = [
  { value: 'simple', label: 'Simples' },
  //{ value: '3d', label: 'Efeito 3D' },
  { value: 'donut', label: 'Efeito Rosca' },
];

const PIE_SLICE_TEXT_TYPES: { value: PieSliceText; label: string }[] = [
  { value: 'value', label: 'Valor' },
  { value: 'percentage', label: 'Percentual' },
];

export const ChartEditor: React.FC<ChartEditorProps> = ({
  config,
  onSave,
  onCancel,
}) => {
  const [title, setTitle] = useState(config.title || '');
  const [chartType, setChartType] = useState(config.type);
  const previousChartTypeRef = useRef<string>(config.type);

  // Gráficos de linha sempre usam múltiplas séries
  const initialMode: ChartMode =
    config.type === 'line' ? 'multiple' : config.mode || 'simple';
  const [chartMode, setChartMode] = useState<ChartMode>(initialMode);

  // Configurações específicas para gráficos de pizza
  const [pieStyle, setPieStyle] = useState<PieStyle>(
    config.pieStyle || 'simple'
  );
  const [pieSliceText, setPieSliceText] = useState<PieSliceText>(
    config.pieSliceText || 'value'
  );
  // Dados para modo simples
  const [data, setData] = useState<ChartData[]>(
    config.data && config.data.length > 0
      ? [...config.data]
      : [
          { label: 'Item 1', value: 30, color: brandColorPalette[0] },
          { label: 'Item 2', value: 50, color: brandColorPalette[1] },
          { label: 'Item 3', value: 20, color: brandColorPalette[2] },
        ]
  );

  // Dados para múltiplas séries
  // Se for gráfico de linha e não tem multipleSeriesData mas tem data, converter
  const getInitialMultipleSeriesData = (): MultipleSeriesChartData => {
    if (config.multipleSeriesData) {
      return config.multipleSeriesData;
    }

    // Se for gráfico de linha e tem data antiga, converter
    if (config.type === 'line' && config.data && config.data.length > 0) {
      return {
        series: [
          {
            name: 'Série 1',
            color: config.data[0]?.color || brandColorPalette[0],
          },
        ],
        categories: config.data.map((item) => item.label),
        values: [config.data.map((item) => item.value)],
      };
    }

    // Default: se for gráfico de linha, usar 1 série e 3 categorias
    if (config.type === 'line') {
      return {
        series: [{ name: 'Série 1', color: brandColorPalette[0] }],
        categories: ['Categoria 1', 'Categoria 2', 'Categoria 3'],
        values: [[30, 50, 20]],
      };
    }

    // Default para outros tipos (column/bar múltiplas séries)
    return {
      series: [
        { name: 'Série 1', color: brandColorPalette[0] },
        { name: 'Série 2', color: brandColorPalette[1] },
      ],
      categories: ['Categoria 1', 'Categoria 2'],
      values: [
        [30, 50],
        [40, 60],
      ],
    };
  };

  const [multipleSeriesData, setMultipleSeriesData] =
    useState<MultipleSeriesChartData>(getInitialMultipleSeriesData());

  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  const chartPreviewRef = useRef<HTMLDivElement>(null);
  const { generateCanvasFromChart } = useChartImageGenerator();

  // Sincronizar configurações de pizza quando o config muda (apenas quando o tipo é pie)
  useEffect(() => {
    if (config.type === 'pie' && chartType === 'pie') {
      if (config.pieStyle !== undefined) {
        setPieStyle(config.pieStyle);
      }
      if (config.pieSliceText !== undefined) {
        setPieSliceText(config.pieSliceText);
      }
    }
  }, [config.pieStyle, config.pieSliceText, config.type, chartType]);

  // Resetar modo quando mudar o tipo de gráfico
  useEffect(() => {
    const previousType = previousChartTypeRef.current;
    previousChartTypeRef.current = chartType;

    // Se mudar para pie, forçar modo simples
    if (chartType === 'pie') {
      setChartMode('simple');
      // Garantir que tem dados simples
      if (!data || data.length === 0) {
        setData([
          { label: 'Item 1', value: 30, color: brandColorPalette[0] },
          { label: 'Item 2', value: 50, color: brandColorPalette[1] },
          { label: 'Item 3', value: 20, color: brandColorPalette[2] },
        ]);
      }
    } else if (chartType === 'line') {
      // Gráficos de linha sempre usam múltiplas séries
      setChartMode('multiple');
      // Se não tem multipleSeriesData mas tem data, converter
      if (!multipleSeriesData.series.length && data.length > 0) {
        const convertedData: MultipleSeriesChartData = {
          series: [
            {
              name: 'Série 1',
              color: data[0]?.color || brandColorPalette[0],
            },
          ],
          categories: data.map((item) => item.label),
          values: [data.map((item) => item.value)],
        };
        setMultipleSeriesData(convertedData);
        setData([]);
      } else if (!multipleSeriesData.series.length) {
        // Se não tem dados, inicializar com padrão
        setMultipleSeriesData({
          series: [{ name: 'Série 1', color: brandColorPalette[0] }],
          categories: ['Categoria 1', 'Categoria 2', 'Categoria 3'],
          values: [[30, 50, 20]],
        });
      }
    } else if (chartType === 'column' || chartType === 'bar') {
      // Se estava em line e mudou para column/bar, resetar para modo simples
      if (previousType === 'line') {
        setChartMode('simple');
        // Inicializar dados simples se não tiver
        if (!data || data.length === 0) {
          setData([
            { label: 'Item 1', value: 30, color: brandColorPalette[0] },
            { label: 'Item 2', value: 50, color: brandColorPalette[1] },
            { label: 'Item 3', value: 20, color: brandColorPalette[2] },
          ]);
        }
      } else {
        // Se não estava em line, usar o modo do config ou padrão simple
        if (config.mode && previousType !== 'line') {
          setChartMode(config.mode);
        } else {
          setChartMode('simple');
          // Garantir que tem dados simples se não tiver
          if (!data || data.length === 0) {
            setData([
              { label: 'Item 1', value: 30, color: brandColorPalette[0] },
              { label: 'Item 2', value: 50, color: brandColorPalette[1] },
              { label: 'Item 3', value: 20, color: brandColorPalette[2] },
            ]);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType]);

  // Funções para modo simples
  const addDataItem = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const newItem: ChartData = {
        label: `Item ${data.length + 1}`,
        value: 10,
        color: brandColorPalette[data.length % brandColorPalette.length],
      };
      setData([...data, newItem]);
    },
    [data]
  );

  const removeDataItem = useCallback(
    (index: number, e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (data.length > 1) {
        setData(data.filter((_, i) => i !== index));
      }
    },
    [data]
  );

  const updateDataItem = useCallback(
    (index: number, field: keyof ChartData, value: string | number) => {
      const newData = [...data];
      newData[index] = { ...newData[index], [field]: value };
      setData(newData);
    },
    [data]
  );

  // Funções para múltiplas séries
  const addSeries = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const newSeries: ChartSeries = {
        name: `Série ${multipleSeriesData.series.length + 1}`,
        color:
          brandColorPalette[
            multipleSeriesData.series.length % brandColorPalette.length
          ],
      };
      const newValues = new Array(multipleSeriesData.categories.length).fill(0);
      setMultipleSeriesData({
        ...multipleSeriesData,
        series: [...multipleSeriesData.series, newSeries],
        values: [...multipleSeriesData.values, newValues],
      });
    },
    [multipleSeriesData]
  );

  const removeSeries = useCallback(
    (index: number, e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (multipleSeriesData.series.length > 1) {
        const newSeries = multipleSeriesData.series.filter(
          (_, i) => i !== index
        );
        const newValues = multipleSeriesData.values.filter(
          (_, i) => i !== index
        );
        setMultipleSeriesData({
          ...multipleSeriesData,
          series: newSeries,
          values: newValues,
        });
      }
    },
    [multipleSeriesData]
  );

  const updateSeries = useCallback(
    (index: number, field: 'name' | 'color', value: string) => {
      const newSeries = [...multipleSeriesData.series];
      newSeries[index] = { ...newSeries[index], [field]: value };
      setMultipleSeriesData({
        ...multipleSeriesData,
        series: newSeries,
      });
    },
    [multipleSeriesData]
  );

  const addCategory = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const newCategory = `Categoria ${multipleSeriesData.categories.length + 1}`;
      const newValues = multipleSeriesData.values.map((serieValues) => [
        ...serieValues,
        0,
      ]);
      setMultipleSeriesData({
        ...multipleSeriesData,
        categories: [...multipleSeriesData.categories, newCategory],
        values: newValues,
      });
    },
    [multipleSeriesData]
  );

  const removeCategory = useCallback(
    (index: number, e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (multipleSeriesData.categories.length > 1) {
        const newCategories = multipleSeriesData.categories.filter(
          (_, i) => i !== index
        );
        const newValues = multipleSeriesData.values.map((serieValues) =>
          serieValues.filter((_, i) => i !== index)
        );
        setMultipleSeriesData({
          ...multipleSeriesData,
          categories: newCategories,
          values: newValues,
        });
      }
    },
    [multipleSeriesData]
  );

  const updateCategory = useCallback(
    (index: number, value: string) => {
      const newCategories = [...multipleSeriesData.categories];
      newCategories[index] = value;
      setMultipleSeriesData({
        ...multipleSeriesData,
        categories: newCategories,
      });
    },
    [multipleSeriesData]
  );

  const updateValue = useCallback(
    (serieIndex: number, categoryIndex: number, value: number) => {
      const newValues = multipleSeriesData.values.map((serieValues, sIdx) => {
        if (sIdx === serieIndex) {
          const newSerieValues = [...serieValues];
          newSerieValues[categoryIndex] = value;
          return newSerieValues;
        }
        return serieValues;
      });
      setMultipleSeriesData({
        ...multipleSeriesData,
        values: newValues,
      });
    },
    [multipleSeriesData]
  );

  const handleSave = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      let newConfig: ChartConfig;

      // Gráficos de linha sempre usam múltiplas séries
      if (chartType === 'line') {
        // Validar múltiplas séries
        if (
          multipleSeriesData.series.length === 0 ||
          multipleSeriesData.categories.length === 0
        ) {
          alert('Por favor, adicione pelo menos uma série e uma categoria');
          return;
        }

        newConfig = {
          type: chartType,
          title: title || undefined,
          mode: 'multiple',
          data: [],
          multipleSeriesData: {
            series: multipleSeriesData.series.filter((s) => s.name.trim()),
            categories: multipleSeriesData.categories.filter((c) => c.trim()),
            values: multipleSeriesData.values,
          },
        };
      } else if (
        chartMode === 'multiple' &&
        (chartType === 'column' || chartType === 'bar')
      ) {
        // Validar múltiplas séries
        if (
          multipleSeriesData.series.length === 0 ||
          multipleSeriesData.categories.length === 0
        ) {
          alert('Por favor, adicione pelo menos uma série e uma categoria');
          return;
        }

        newConfig = {
          type: chartType,
          title: title || undefined,
          mode: 'multiple',
          data: [],
          multipleSeriesData: {
            series: multipleSeriesData.series.filter((s) => s.name.trim()),
            categories: multipleSeriesData.categories.filter((c) => c.trim()),
            values: multipleSeriesData.values,
          },
        };
      } else {
        // Modo simples (pie, column simples, bar simples)
        const filteredData = data.filter(
          (item) => item.label.trim() && item.value > 0
        );

        if (filteredData.length === 0) {
          alert('Por favor, adicione pelo menos um item ao gráfico');
          return;
        }

        newConfig = {
          type: chartType,
          title: title || undefined,
          mode: chartType === 'pie' ? undefined : 'simple',
          data: filteredData,
          ...(chartType === 'pie' && {
            pieStyle,
            pieSliceText,
          }),
        };
      }

      setIsGeneratingImage(true);

      try {
        let imagePath: string | undefined;

        if (chartPreviewRef.current) {
          try {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const chartPreviewElement = chartPreviewRef.current;
            imagePath = await generateCanvasFromChart(
              chartPreviewElement as HTMLElement,
              newConfig
            );
          } catch (error) {
            console.warn('Não foi possível gerar imagem do gráfico:', error);
          }
        }
        onSave(newConfig, imagePath);
      } catch (error) {
        alert('Erro ao salvar o gráfico. Tente novamente.');
      } finally {
        setIsGeneratingImage(false);
      }
    },
    [
      title,
      chartType,
      chartMode,
      data,
      multipleSeriesData,
      pieStyle,
      pieSliceText,
      onSave,
      generateCanvasFromChart,
    ]
  );

  const handleCancel = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    },
    [onCancel]
  );

  const shouldShowModeSelector = chartType === 'column' || chartType === 'bar';

  const getPreviewConfig = (): ChartConfig => {
    // Gráficos de linha sempre usam múltiplas séries
    if (chartType === 'line') {
      return {
        type: chartType,
        title,
        mode: 'multiple',
        data: [],
        multipleSeriesData,
      };
    }

    if (
      chartMode === 'multiple' &&
      (chartType === 'column' || chartType === 'bar')
    ) {
      return {
        type: chartType,
        title,
        mode: 'multiple',
        data: [],
        multipleSeriesData,
      };
    }
    return {
      type: chartType,
      title,
      mode: chartType === 'pie' ? undefined : 'simple',
      data,
      ...(chartType === 'pie' && {
        pieStyle,
        pieSliceText,
      }),
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex gap-2 justify-end w-full">
          <Button onClick={handleCancel} variant="ghost" buttonSize="small">
            Cancelar
          </Button>
          <Button
            onClick={(e) => handleSave(e)}
            disabled={isGeneratingImage}
            variant="primary"
            buttonSize="small"
          >
            {isGeneratingImage ? (
              <>
                <Icon name="Loader" className="animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Icon name="Save" />
                Salvar
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Seletor de tipo */}
      <div
        className="flex justify-center -mt-6"
        contentEditable={false}
        suppressContentEditableWarning={true}
        data-drag-handle
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => e.preventDefault()}
      >
        <div
          className="inline-flex bg-white border border-gray-200 rounded-lg p-1"
          contentEditable={false}
          suppressContentEditableWarning={true}
        >
          {CHART_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              contentEditable={false}
              suppressContentEditableWarning={true}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setChartType(type.value as ChartConfig['type']);
              }}
              className={`flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                chartType === type.value
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              title={type.label}
            >
              {type.value === 'pie' && <Icon name="ChartPie" />}
              {type.value === 'column' && <Icon name="ChartColumn" />}
              {type.value === 'bar' && <Icon name="ChartBar" />}
              {type.value === 'line' && <Icon name="ChartLine" />}
            </button>
          ))}
        </div>
      </div>

      {/* Seletor de modo (apenas para column e bar) */}
      {shouldShowModeSelector && (
        <div
          className="flex justify-center -mt-2"
          contentEditable={false}
          suppressContentEditableWarning={true}
          data-drag-handle
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.preventDefault()}
        >
          <div
            className="inline-flex bg-white border border-gray-200 rounded-lg p-1"
            contentEditable={false}
            suppressContentEditableWarning={true}
          >
            {CHART_MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                contentEditable={false}
                suppressContentEditableWarning={true}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setChartMode(mode.value);
                }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  chartMode === mode.value
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Seletor de estilo do gráfico de pizza */}
      {chartType === 'pie' && (
        <div
          className="flex justify-center -mt-2"
          contentEditable={false}
          suppressContentEditableWarning={true}
          data-drag-handle
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.preventDefault()}
        >
          <div
            className="inline-flex bg-white border border-gray-200 rounded-lg p-1"
            contentEditable={false}
            suppressContentEditableWarning={true}
          >
            {PIE_STYLES.map((style) => (
              <button
                key={style.value}
                type="button"
                contentEditable={false}
                suppressContentEditableWarning={true}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPieStyle(style.value);
                }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  pieStyle === style.value
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {style.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Seletor de tipo de fatia do gráfico de pizza */}
      {chartType === 'pie' && (
        <div
          className="flex justify-center -mt-2"
          contentEditable={false}
          suppressContentEditableWarning={true}
          data-drag-handle
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.preventDefault()}
        >
          <div
            className="inline-flex bg-white border border-gray-200 rounded-lg p-1"
            contentEditable={false}
            suppressContentEditableWarning={true}
          >
            {PIE_SLICE_TEXT_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                contentEditable={false}
                suppressContentEditableWarning={true}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPieSliceText(type.value);
                }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  pieSliceText === type.value
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
        {/* Preview do gráfico */}
        <div className="-mt-2 flex justify-center">
          <div
            ref={chartPreviewRef}
            className="border border-gray-200 rounded-md p-4 bg-gray-50 max-w-[720px] w-full"
          >
            <ChartPreview config={getPreviewConfig()} />
          </div>
        </div>

        {/* Configurações principais */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Título do gráfico (opcional)"
            />
          </div>

        </div>
      </div>

      {/* Dados do gráfico */}
      {chartType === 'line' ||
      (chartMode === 'multiple' &&
        (chartType === 'column' || chartType === 'bar')) ? (
        // Interface para múltiplas séries
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Dados do Gráfico
            </label>
            <div className="flex gap-2">
              <Button
                onClick={(e) => addCategory(e)}
                variant="ghost"
                buttonSize="small"
              >
                <Icon name="Plus" />
                Adicionar Categoria
              </Button>
              <Button
                onClick={(e) => addSeries(e)}
                variant="primary"
                buttonSize="small"
              >
                <Icon name="Plus" />
                Adicionar Série
              </Button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-md bg-white">
            <div className="flex">
              {/* Coluna fixa - Série */}
              <div className="flex-shrink-0 border-r border-gray-200 bg-gray-50">
                <table className="w-[280px] table-fixed">
                  <thead>
                    <tr className="h-[57px]">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 border-b border-gray-200 bg-gray-50 h-[57px] align-middle">
                        Série
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {multipleSeriesData.series.map((serie, serieIdx) => (
                      <tr
                        key={serieIdx}
                        className="border-b border-gray-200 h-[57px]"
                      >
                        <td className="px-3 py-2 h-[57px] align-middle">
                          <div className="flex items-center gap-2 h-full">
                            <ColorPicker
                              w="30px"
                              flexShrink={0}
                              defaultColor={serie.color}
                              colors={brandColorPalette}
                              onColorChange={(color) =>
                                updateSeries(serieIdx, 'color', color.hex)
                              }
                            />
                            <input
                              type="text"
                              value={serie.name}
                              onChange={(e) =>
                                updateSeries(serieIdx, 'name', e.target.value)
                              }
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Nome da série"
                            />
                            {multipleSeriesData.series.length > 1 && (
                              <Toolbar.Button
                                onClick={(e) => removeSeries(serieIdx, e)}
                                variant="ghost"
                                buttonSize="icon"
                                className="text-red-500 hover:bg-red-500/10 hover:text-red-600"
                              >
                                <Icon name="Trash2" className="w-4 h-4" />
                              </Toolbar.Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Área com scroll - Categorias */}
              <div className="flex-1 overflow-x-auto overflow-y-visible">
                <table className="min-w-full table-fixed">
                  <thead>
                    <tr className="bg-gray-50 h-[57px]">
                      {multipleSeriesData.categories.map((category, catIdx) => (
                        <th
                          key={catIdx}
                          className="px-3 py-2 text-center text-xs font-medium text-gray-700 border-b border-gray-200 border-l min-w-[150px] h-[57px] align-middle"
                        >
                          <div className="flex items-center justify-center gap-2 h-full">
                            <input
                              type="text"
                              value={category}
                              onChange={(e) =>
                                updateCategory(catIdx, e.target.value)
                              }
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
                              placeholder="Categoria"
                            />
                            {multipleSeriesData.categories.length > 1 && (
                              <Toolbar.Button
                                onClick={(e) => removeCategory(catIdx, e)}
                                variant="ghost"
                                buttonSize="icon"
                                className="text-red-500 hover:bg-red-500/10 hover:text-red-600"
                              >
                                <Icon name="Trash2" className="w-4 h-4" />
                              </Toolbar.Button>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {multipleSeriesData.series.map((_, serieIdx) => (
                      <tr
                        key={serieIdx}
                        className="border-b border-gray-200 h-[57px]"
                      >
                        {multipleSeriesData.categories.map((_, catIdx) => (
                          <td
                            key={catIdx}
                            className="px-3 py-2 text-center border-l border-gray-200 h-[57px] align-middle"
                          >
                            <input
                              type="number"
                              value={
                                multipleSeriesData.values[serieIdx]?.[catIdx] ||
                                0
                              }
                              onChange={(e) =>
                                updateValue(
                                  serieIdx,
                                  catIdx,
                                  Number(e.target.value) || 0
                                )
                              }
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
                              placeholder="0"
                              min="0"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Interface para modo simples
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Dados do Gráfico
            </label>
            <Button
              onClick={(e) => addDataItem(e)}
              variant="primary"
              buttonSize="small"
            >
              <Icon name="Plus" />
              Adicionar Item
            </Button>
          </div>

          <div className="space-y-2">
            {data.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-md"
              >
                <div className="flex-1">
                  <input
                    type="text"
                    value={item.label}
                    onChange={(e) =>
                      updateDataItem(index, 'label', e.target.value)
                    }
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Rótulo"
                  />
                </div>
                <div className="w-24">
                  <input
                    type="number"
                    value={item.value}
                    onChange={(e) =>
                      updateDataItem(index, 'value', Number(e.target.value))
                    }
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Valor"
                    min="0"
                  />
                </div>
                <div>
                  <ColorPicker
                    w="30px"
                    defaultColor={item.color}
                    colors={brandColorPalette}
                    onColorChange={(color) =>
                      updateDataItem(index, 'color', color.hex)
                    }
                  />
                </div>
                <Toolbar.Button
                  onClick={(e) => removeDataItem(index, e)}
                  disabled={data.length === 1}
                  variant="ghost"
                  buttonSize="icon"
                  className="text-red-500 hover:bg-red-500/10 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="Trash2" />
                </Toolbar.Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartEditor;
